import { tx as translateInterfaceText } from '@/utils/i18n';
import React from 'react';
import {
  ListVideo,
  Image,
  Play,
  RefreshCw,
  Film,
  Clock3,
  Zap,
  PaintBucket,
  Type,
  MousePointerClick,
  UserPlus,
  Volume2,
  Smartphone,
  PauseCircle,
  Captions,
  Magnet,
  PlaySquare,
  ThumbsUp,
  Bell,
} from 'lucide-react';
import { FeatureDefinition } from './types';

export const featureDefinitions: FeatureDefinition[] = [
  // ===== PLAYER =====
  {
    id: 'controls',
    get title() { return translateInterfaceText("Controles"); },
    get description() { return translateInterfaceText("Configurações de controle do player"); },
    icon: <Play className="h-4 w-4" />,
    category: 'player',
    isRecommended: true,
    tier: 'core',
    order: 1,
  },
  {
    id: 'autoplayOverlay',
    get title() { return translateInterfaceText("Autoplay"); },
    get description() { return translateInterfaceText("Inicia o vídeo automaticamente ao entrar na página."); },
    icon: <Volume2 className="h-4 w-4" />,
    category: 'player',
    tier: 'core',
    get microTip() { return translateInterfaceText("Ideal para tráfego quente e vídeos curtos"); },
    order: 2,
  },
  {
    id: 'progressBar',
    get title() { return translateInterfaceText("Barra Fake"); },
    get description() { return translateInterfaceText("Configurações da barra de progresso"); },
    icon: <ListVideo className="h-4 w-4" />,
    category: 'player',
    isRecommended: true,
    tier: 'core',
    get microTip() { return translateInterfaceText("Aumenta retenção em vídeos longos"); },
    order: 3,
  },
  {
    id: 'continueWatching',
    get title() { return translateInterfaceText("Continuar Assistindo"); },
    get description() { return translateInterfaceText("Permite que o usuário continue de onde parou"); },
    icon: <Clock3 className="h-4 w-4" />,
    category: 'player',
    isNew: true,
    isRecommended: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Retém usuários que saíram do vídeo"); },
    groupEnd: true,
    order: 4,
  },
  {
    id: 'haveRestart',
    get title() { return translateInterfaceText("Recomeçar após finalizar"); },
    get description() { return translateInterfaceText("Defina se o vídeo deve voltar ao início automaticamente quando a reprodução terminar."); },
    icon: <RefreshCw className="h-4 w-4" />,
    category: 'player',
    tier: 'complementary',
    order: 5,
  },
  {
    id: 'smartPause',
    get title() { return translateInterfaceText("Pausa Inteligente"); },
    get description() { return translateInterfaceText("Pausa o vídeo automaticamente quando o espectador sai da aba do navegador."); },
    icon: <PauseCircle className="h-4 w-4" />,
    category: 'player',
    isNew: true,
    tier: 'complementary',
    order: 6,
  },
  {
    id: 'playbackSpeed',
    get title() { return translateInterfaceText("Velocidade"); },
    get description() { return translateInterfaceText("Permita que o usuário controle a velocidade do vídeo"); },
    icon: <Zap className="h-4 w-4" />,
    category: 'player',
    isPremium: true,
    tier: 'complementary',
    order: 7,
  },

  // ===== VISUAL =====
  {
    id: 'thumbnail',
    get title() { return translateInterfaceText("Thumbnail"); },
    get description() { return translateInterfaceText("Configuração da thumbnail do vídeo"); },
    icon: <Film className="h-4 w-4" />,
    category: 'visual',
    isPopular: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Define a primeira impressão antes do play"); },
    order: 10,
  },
  {
    id: 'border',
    get title() { return translateInterfaceText("Borda customizada"); },
    get description() { return translateInterfaceText("Adicione uma borda animada e personalizada"); },
    icon: <PaintBucket className="h-4 w-4" />,
    category: 'visual',
    isNew: true,
    isPremium: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Destaque visual para chamar atenção"); },
    order: 11,
  },
  {
    id: 'logo',
    get title() { return translateInterfaceText("Logo Marca"); },
    get description() { return translateInterfaceText("Adicione sua logo marca no vídeo"); },
    icon: <Image className="h-4 w-4" />,
    category: 'visual',
    tier: 'complementary',
    order: 12,
  },
  {
    id: 'subtitles',
    get title() { return translateInterfaceText("Legendas"); },
    get description() { return translateInterfaceText("Adicione legendas sincronizadas ao seu vídeo. Suporta .SRT e .VTT."); },
    icon: <Captions className="h-4 w-4" />,
    category: 'visual',
    isNew: true,
    tier: 'complementary',
    order: 13,
  },
  {
    id: 'responsivePlayer',
    get title() { return translateInterfaceText("Exibição por Dispositivo"); },
    get description() { return translateInterfaceText("Controle como o player aparece em cada dispositivo: Desktop, Tablet e Celular"); },
    icon: <Smartphone className="h-4 w-4" />,
    category: 'visual',
    isNew: true,
    isPremium: true,
    tier: 'complementary',
    order: 14,
  },

  // ===== CONVERSÃO =====
  {
    id: 'actionButton',
    get title() { return translateInterfaceText("Botão de Ação"); },
    get description() { return translateInterfaceText("Adicione um botão de compra ou call-to-action"); },
    icon: <MousePointerClick className="h-4 w-4" />,
    category: 'conversion',
    isPopular: true,
    tier: 'core',
    get microTip() { return translateInterfaceText("Principal gatilho de conversão"); },
    order: 20,
  },
  {
    id: 'headline',
    get title() { return translateInterfaceText("Headline"); },
    get description() { return translateInterfaceText("Adicione títulos chamativos com upload de imagem e A/B test integrado"); },
    icon: <Type className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    isPremium: true,
    tier: 'complementary',
    order: 21,
  },
  {
    id: 'audience',
    get title() { return translateInterfaceText("Simulação de Audiência"); },
    get description() { return translateInterfaceText("Mostre um contador de pessoas assistindo o vídeo"); },
    icon: <UserPlus className="h-4 w-4" />,
    category: 'conversion',
    isPremium: true,
    tier: 'complementary',
    groupEnd: true,
    order: 22,
  },
  {
    id: 'miniHooks',
    get title() { return translateInterfaceText("Mini Hooks"); },
    get description() { return translateInterfaceText("Exiba imagens ou GIFs em momentos estratégicos do vídeo para reforçar a atenção"); },
    icon: <Magnet className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    isPremium: true,
    tier: 'complementary',
    order: 23,
  },
  {
    id: 'conversionJourney',
    get title() { return translateInterfaceText("Jornada de Conversão"); },
    get description() { return translateInterfaceText("Crie vídeos de entrada e saída para conduzir o espectador até a próxima ação"); },
    icon: <PlaySquare className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    tier: 'complementary',
    order: 23.5,
  },
  {
    id: 'videoFeedback',
    get title() { return translateInterfaceText("Feedback do Vídeo"); },
    get description() { return translateInterfaceText("Colete avaliações dos espectadores diretamente no player"); },
    icon: <ThumbsUp className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Meça a percepção real do conteúdo"); },
    order: 24,
  },
  {
    id: 'salesNotification',
    get title() { return translateInterfaceText("Notificação de Vendas"); },
    get description() { return translateInterfaceText("Exiba provas sociais de compras recentes enquanto o vídeo roda"); },
    icon: <Bell className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Prova social no momento da oferta"); },
    order: 24.5,
  },
  {
    id: 'finalCountdown',
    get title() { return translateInterfaceText("Contagem Final Inteligente"); },
    get description() { return translateInterfaceText("Ao terminar o vídeo, mostre uma contagem regressiva e leve o espectador ao checkout"); },
    icon: <Clock3 className="h-4 w-4" />,
    category: 'conversion',
    isNew: true,
    tier: 'complementary',
    get microTip() { return translateInterfaceText("Fecha o vídeo com uma ação clara"); },
    order: 24.6,
  },
];

export const getFeatureDefinition = (id: string): FeatureDefinition | undefined => {
  if (!featureDefinitions || !Array.isArray(featureDefinitions)) {
    console.error('[getFeatureDefinition] featureDefinitions is undefined or not an array');
    return undefined;
  }

  return featureDefinitions.find(feature => feature.id === id);
};
